import { initializeYjsDocumentStageData } from '@hendrx/core/common';
import { ScoreWeightSummary } from '@hendrx/core/common/components/content/ScoreWeightSummary';
import { DndPreview } from '@hendrx/core/common/components/dnd/DndPreview';
import { Pages } from '@hendrx/core/common/components/pages/Pages';
import { RichTextToolbarTarget } from '@hendrx/core/common/components/richTexts/RichTextToolbarTarget/RichTextToolbarTarget';
import { SideMenu } from '@hendrx/core/common/components/studio/SideMenu';
import { CreateThemeContent } from '@hendrx/core/common/components/themes/Theme/CreateThemeContent/CreateThemeContent';
import { UpdateThemeContent } from '@hendrx/core/common/components/themes/Theme/UpdateThemeContent/UpdateThemeContent';
import { stageContainerHeightVar, widgetManifests } from '@hendrx/core/common/contentObjects';
import { PlenaryPageWrapper } from '@hendrx/core/plenary/components/PlenaryPageWrapper';
import { PlenaryStudioEditProviders } from '@hendrx/core/plenary/components/PlenaryStudioEditProviders';
import { PlenaryStudioTopBar } from '@hendrx/core/plenary/components/PlenaryStudioTopBar';
import { PlenaryToolbox } from '@hendrx/core/plenary/components/PlenaryToolbox';
import { useGetPlenaryDraftByIdQuery } from '@hendrx/core/plenary/queries/getPlenaryDraftById';
import { Plugins } from '@hendrx/core/plugins/components/Plugins';
import { AppShell, Center, Container, rem } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CustomLoadingOverlay } from '@hendrx/shared/components/app/CustomLoadingOverlay';
import { HEADER_HEIGHT_PX } from 'shared/client/consts/styleConsts';
import { modals } from 'shared/client/utils/modals.utils';
import { useLearningObjectCommonData } from 'shared/components/providers/LearningObjectCommonDataProvider';
import { sidebarWidth } from 'shared/utils/theme.utils';
import { useUiLanguage } from 'shared/utils/useUiLanguage';

import { useStore } from '@/store';

export const Route = createFileRoute('/studio/plenary/edit/$plenaryDraftId/')({
  component: PlenaryStudioEditPage,
  validateSearch: z.object({
    themeId: z.string().optional()
  })
});

function PlenaryStudioEditPage() {
  const { plenaryDraftId } = Route.useParams();
  const search = Route.useSearch();
  const themeId = search.themeId;
  const withStaticContent = !!themeId;

  return (
    <PlenaryStudioEditProviders
      plenaryDraftId={plenaryDraftId}
      initializeYjsDocumentStageData={initializeYjsDocumentStageData}
      withStaticContent={withStaticContent}
    >
      <PlenaryStudioEditPageContent themeId={themeId} />
    </PlenaryStudioEditProviders>
  );
}

function PlenaryStudioEditPageContent({ themeId }: { themeId?: string }) {
  const { t } = useTranslation('components');
  const { learningObjectDraftId: plenaryDraftId } = useLearningObjectCommonData();
  const { isLoading, error } = useGetPlenaryDraftByIdQuery(plenaryDraftId);
  const setDeviceView = useStore(state => state.setDeviceView);

  const { height } = useViewportSize();

  useEffect(() => {
    setDeviceView('pc');
  }, [setDeviceView]);

  const { uiDirection } = useUiLanguage();

  if (error) {
    return (
      <Container h={height} size="100%">
        <Center h="100%">{t('studio.learningObjectDraftFetchError')}</Center>
      </Container>
    );
  }

  return (
    <ModalsProvider modals={modals}>
      <AppShell dir={uiDirection} padding="0" withBorder={false}>
        <AppShell.Header>
          <PlenaryStudioTopBar />
        </AppShell.Header>
        <AppShell.Navbar>
          <PlenaryToolbox
            widgetManifests={widgetManifests}
            pluginsContent={<Plugins category="embed" />}
            scoreWeight={<ScoreWeightSummary />}
            pages={Pages}
          />
        </AppShell.Navbar>
        <AppShell.Main mih="0">
          <Container
            style={{
              height: `calc(${height}px - ${rem(HEADER_HEIGHT_PX)})`,
              [stageContainerHeightVar]: `calc(${height}px - ${rem(HEADER_HEIGHT_PX)})`,
              marginTop: rem(HEADER_HEIGHT_PX),
              marginInlineStart: rem(sidebarWidth),
              padding: '0',
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr min-content'
            }}
            size="100%"
          >
            {!isLoading ? (
              <>
                <RichTextToolbarTarget />
                {!themeId && <PlenaryPageWrapper />}
                {themeId &&
                  (themeId === 'new' ? (
                    <CreateThemeContent themeId={themeId} currentContentPreviewComponent={<PlenaryPageWrapper />} />
                  ) : (
                    <UpdateThemeContent themeId={themeId} currentContentPreviewComponent={<PlenaryPageWrapper />} />
                  ))}
              </>
            ) : (
              <CustomLoadingOverlay visible />
            )}
            <SideMenu />
          </Container>
        </AppShell.Main>
      </AppShell>
      <DndPreview />
    </ModalsProvider>
  );
}
